import React, { useState } from 'react'
import { Button, Stack, Typography, useTheme } from '@mui/material'
import JelledModal, { ModalButtonsContainerStack } from 'components/JelledModal'
import { isFieldEmpty } from 'utils'
import { useActiveOrganization } from 'hooks/use-organization'
import { toastAtom } from 'state'
import { useSetRecoilState } from 'recoil'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { TwinPrompt, TwinPromptType } from 'api/model'
import { twinsNewPrompt, twinsUpdatePrompt } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { TextFieldWithDarkMode } from 'dark-mode-components'
import Selector from 'components/Selector'
import { COLORS } from 'app-constants'

const TwinPromptTypeOptions = Object.values(TwinPromptType)

const AddOrEditTwinPromptModal = ({
  onClose,
  onAddTwinPrompt,
  onEditTwinPrompt,
  twinPrompt,
}: {
  onClose: () => void
  onAddTwinPrompt: (prompt: TwinPrompt) => void
  onEditTwinPrompt: (prompt: TwinPrompt) => void
  twinPrompt?: TwinPrompt
}) => {
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()

  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitCount, setSubmitCount] = useState(0)

  const [name, setName] = useState<string>(twinPrompt?.name ?? '')
  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }

  const [promptType, setPromptType] = useState<TwinPromptType>(twinPrompt?.type ?? TwinPromptType.jinja2)

  const [description, setDescription] = useState<string>(twinPrompt?.description ?? '')
  const onDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value)
  }

  const [content, setContent] = useState<string>(twinPrompt?.content ?? '')
  const onContentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContent(event.target.value)
  }

  const onAddNewTwinPrompt = async () => {
    if (!activeOrganization) {
      return null
    }

    try {
      const response = await twinsNewPrompt(activeOrganization, {
        name: name,
        type: promptType,
        description: description,
        content: content,
      })
      onAddTwinPrompt(response.data)
      setToast({ title: 'Added twin prompt', severity: 'success' })
      onClose()
    } catch (error) {
      setToast({ title: 'Failed to add twin prompt', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onEditExistingTwinPrompt = async () => {
    if (!activeOrganization || !twinPrompt) {
      return null
    }

    try {
      const response = await twinsUpdatePrompt(activeOrganization, twinPrompt.id, {
        name: name,
        type: promptType,
        description: description,
        content: content,
      })
      onEditTwinPrompt(response.data)
      setToast({ title: 'Edited twin prompt', severity: 'success' })
      onClose()
    } catch (error) {
      setToast({ title: 'Failed to edit twin prompt', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onFormSubmit = async () => {
    setIsSubmitting(true)
    setSubmitCount((submitCount) => submitCount + 1)

    if (!activeOrganization || isFieldEmpty(name) || isFieldEmpty(content)) {
      setIsSubmitting(false)
      return
    }

    if (twinPrompt) {
      onEditExistingTwinPrompt()
    } else {
      onAddNewTwinPrompt()
    }
  }

  return (
    <JelledModal
      onClose={onClose}
      isOpen={true}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
        marginLeft: isMobile ? '16px' : 0,
        marginRight: isMobile ? '16px' : 0,
      }}>
      <Stack>
        <Typography mb={4} variant="h4" color="secondary">
          {twinPrompt ? 'Edit twin prompt' : 'New twin prompt'}
        </Typography>

        <TextFieldWithDarkMode
          className="has-label"
          isDarkBackdrop={false}
          theme={theme}
          label="Name"
          placeholder="Enter the name of the prompt"
          value={name}
          onChange={onNameChange}
          disabled={isSubmitting}
          error={submitCount > 0 && isFieldEmpty(name)}
          helperText={submitCount > 0 && isFieldEmpty(name) && 'You must enter a name'}
          sx={{
            mb: 4,
          }}
        />

        <Stack mb={4}>
          <TwinPromptTypeSelector promptType={promptType} setPromptType={setPromptType} />
        </Stack>

        <TextFieldWithDarkMode
          className="has-label"
          isDarkBackdrop={false}
          theme={theme}
          label="Description"
          placeholder="Enter the description of the prompt"
          value={description}
          onChange={onDescriptionChange}
          disabled={isSubmitting}
          sx={{
            mb: 4,
          }}
        />

        <TextFieldWithDarkMode
          className="has-label"
          isDarkBackdrop={false}
          theme={theme}
          multiline
          rows={36}
          label="Content"
          placeholder="Prompt content goes here"
          value={content}
          onChange={onContentChange}
          disabled={isSubmitting}
          error={submitCount > 0 && isFieldEmpty(content)}
          helperText={submitCount > 0 && isFieldEmpty(content) && 'You must enter some content'}
        />

        <ModalButtonsContainerStack>
          <Button color="secondary" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button variant="contained" size="medium" color="primary" onClick={onFormSubmit}>
            Done
          </Button>
        </ModalButtonsContainerStack>
      </Stack>
    </JelledModal>
  )
}

const TwinPromptTypeSelector = ({
  promptType,
  setPromptType,
}: {
  promptType: TwinPromptType
  setPromptType: (promptType: TwinPromptType) => void
}) => {
  return (
    <Selector
      id="prompt-type-selector"
      option={promptType}
      label="Type"
      setOption={setPromptType}
      options={TwinPromptTypeOptions}
      isDarkBackdrop={false}
      width="240px"
    />
  )
}

export default AddOrEditTwinPromptModal
